import { useCallback } from "react";
import { arrayIncludes } from "ts-extras";
import { useSendEvent } from "../../common/events/useSendEvent";
import { useGlobalState } from "../../state/hooks/useGlobalState";
import type { LayerName } from "../products/types";
import type { AvailableLayerCombinations } from "./types";

type UseLayersReturn = {
    isLayerShown: (layerName: LayerName) => boolean;
    showLayers: (...layers: AvailableLayerCombinations) => void;
    shownLayerCombination: AvailableLayerCombinations;
    shownLayers: LayerName[];
    isLayerCombinationShown: (
        ...layerCombination: AvailableLayerCombinations
    ) => boolean;
    showCloudsTogetherWithPrecipitation: boolean;
    setShowCloudsLayerTogetherWithPrecipitationLayer: (on: boolean) => void;
};

export function useLayers(): UseLayersReturn {
    const sendEvent = useSendEvent();
    const {
        setShowCloudsTogetherWithPrecipitation,
        setShownLayerCombination,
        shownLayerCombination,
        showCloudsTogetherWithPrecipitation,
        setLastShownLayerCombination,
    } = useGlobalState();

    const showLayersWithEvent = useCallback(
        (layers: AvailableLayerCombinations) => {
            setShownLayerCombination((prevLayers) => {
                setLastShownLayerCombination(prevLayers);

                return layers;
            });

            sendEvent({
                type: "shownLayersChanged",
                toLayers: layers,
            });
        },
        [sendEvent, setLastShownLayerCombination, setShownLayerCombination],
    );

    const isLayerShown = useCallback<UseLayersReturn["isLayerShown"]>(
        (layerName) => arrayIncludes(shownLayerCombination, layerName),
        [shownLayerCombination],
    );

    const isLayerCombinationShown = useCallback<
        UseLayersReturn["isLayerCombinationShown"]
    >(
        (...layerCombination) =>
            shownLayerCombination.length === layerCombination.length &&
            shownLayerCombination.every((shownLayerName) =>
                arrayIncludes(layerCombination, shownLayerName),
            ),
        [shownLayerCombination],
    );

    const showLayers = useCallback<UseLayersReturn["showLayers"]>(
        (...layers) => {
            if (layers[0] === "precipitation") {
                // show precipitation + clouds layer together if they were shown together previously
                showLayersWithEvent(
                    showCloudsTogetherWithPrecipitation
                        ? ["precipitation", "clouds"]
                        : ["precipitation"],
                );
            } else {
                showLayersWithEvent(layers);
            }
        },
        [showCloudsTogetherWithPrecipitation, showLayersWithEvent],
    );

    const setShowCloudsLayerTogetherWithPrecipitationLayer = useCallback<
        UseLayersReturn["setShowCloudsLayerTogetherWithPrecipitationLayer"]
    >(
        (on) => {
            setShowCloudsTogetherWithPrecipitation(on);

            showLayersWithEvent(
                on ? ["precipitation", "clouds"] : ["precipitation"],
            );
        },
        [setShowCloudsTogetherWithPrecipitation, showLayersWithEvent],
    );

    return {
        isLayerShown,
        isLayerCombinationShown,
        shownLayerCombination,
        showLayers,
        showCloudsTogetherWithPrecipitation,
        setShowCloudsLayerTogetherWithPrecipitationLayer,
        shownLayers: shownLayerCombination,
    };
}
